// ---------------------------------------------------------
// Datei: api.extensions.ts
// Zweck: Erweiterungen für API-Service (Upload-Funktionen)
// Besonderheiten:
// - Verbindet Frontend-Upload mit Backend-Route
import { getEnv } from "../utils/env";
// - Konvertiert Base64-Bilder zu Blob für Multipart-Upload
// - Backend-Route leitet an HubSpot weiter
// Stand: 28. November 2025
// ---------------------------------------------------------

import api from "./api";

/**
 * Upload-Response vom Backend (HubSpot-Endpunkt)
 */
interface UploadResponse {
  success: boolean;
  fileId: string;
  dealId: string;
  property: string;
  warning?: string;
}



/**
 * Bild hochladen zu HubSpot (via Backend)
 *
 * Ablauf:
 * 1. Base64-String wird zu Blob konvertiert
 * 2. Blob wird als FormData an Backend gesendet
 * 3. Backend lädt zu HubSpot hoch und patcht Deal
 * 4. FileId wird zurückgegeben für spätere Ansicht
 *
 * @param phaseId - Format: "{dealId}-{phaseName}" (z.B. "313739230424-vorab")
 * @param checklistItemId - ID aus document-types.json (z.B. "typenschild-altanlage")
 * @param imageData - Base64-String des Bildes
 * @param fileName - Dateiname für Upload
 * @returns FileId der hochgeladenen Datei (für View-Funktion)
 */
export const uploadImage = async (
  phaseId: string,
  checklistItemId: string,
  imageData: string,
  fileName: string
): Promise<string | void> => {
  // FormData erstellen
  const formData = new FormData();

  // Base64-String in Blob umwandeln
  const base64Response = await fetch(imageData);
  const blob = await base64Response.blob();

  // Blob zum FormData hinzufügen
  formData.append("image", blob, fileName);

  // Bild hochladen (Backend leitet an HubSpot weiter)
  const response = await api.post<UploadResponse>(
    `/phases/${phaseId}/checklist/${checklistItemId}/image`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  // FileId für spätere Ansicht zurückgeben
  console.log(
    `[Upload] Success: fileId=${response.fileId}, dealId=${response.dealId}, property=${response.property}`
  );

  return response.fileId;
};

/**
 * Dokument löschen (Property im Deal leeren)
 *
 * @param phaseId - Format: "{dealId}-{phaseName}"
 * @param checklistItemId - ID aus document-types.json
 */
export const deleteDocument = async (
  phaseId: string,
  checklistItemId: string
): Promise<void> => {
  // Property-Name mappen (muss mit Backend übereinstimmen)
  // Wir nutzen hier eine vereinfachte Logik, idealerweise sollte das Mapping zentral sein
  // Aber da das Backend das Mapping macht, rufen wir einen speziellen Endpoint auf
  // ODER wir nutzen den existierenden /phases Endpoint mit DELETE?
  // Nein, wir nutzen den neuen /api/hubspot/deal/clear Endpoint, aber wir müssen den Property-Namen wissen.
  // Das ist schwierig, da das Mapping im Backend (hubspotDealMapper) oder Frontend (hubspotDealMapper) liegt.
  // Besser: Wir nutzen einen neuen Endpoint im phases.express.ts, der das Mapping übernimmt.

  // Workaround: Wir rufen den phases-Endpoint mit DELETE auf.
  // Aber phases.express.ts hat noch kein DELETE.
  // Wir fügen es dort hinzu.
  await api.delete(`/phases/${phaseId}/checklist/${checklistItemId}`);
};

/**
 * Datei zu Google Drive hochladen (via Backend /api/drive/)
 *
 * @param dealName   - Deal-Name für Drive-Ordnerstruktur (z.B. "Wärmepumpeninstallation Müller")
 * @param imageData  - Base64-Data-URL des Bildes
 * @param fileName   - Dateiname
 * @param directories - Optionale Unterordner (z.B. ["Vorbereitung", "heizlastberechnung"])
 * @returns fileId der hochgeladenen Datei oder null bei Fehler
 */
export const uploadToDrive = async (
  dealName: string,
  imageData: string,
  fileName: string,
  directories?: string[],
  sharedDrive?: string
): Promise<{ fileId: string; webViewLink: string } | null> => {
  const AUTH_BASE = import.meta.env.DEV
    ? '/auth-api'
    : (getEnv('VITE_AUTH_API_URL') || '/auth-api');

  const formData = new FormData();

  // Base64 → Blob → File
  const base64Response = await fetch(imageData);
  const blob = await base64Response.blob();
  formData.append("files", new File([blob], fileName, { type: blob.type }));
  formData.append("dealName", dealName);

  if (directories?.length) {
    formData.append("directories", JSON.stringify(directories));
  }

  if (sharedDrive) {
    formData.append("sharedDrive", sharedDrive);
  }

  const response = await fetch(`${AUTH_BASE}/google/drive/upload`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Drive Upload fehlgeschlagen: ${response.status} - ${text}`);
  }

  const json = await response.json();

  if (!json.status) {
    throw new Error(json.message || "Drive Upload fehlgeschlagen");
  }

  const driveFile = json.files?.[0] ?? null;

  if (!driveFile?.fileId) return null;
  return { fileId: driveFile.fileId, webViewLink: `https://drive.google.com/file/d/${driveFile.fileId}` };
};

// Weitere API-Erweiterungen hier hinzufügen

export const deleteDriveFile = async (fileId: string): Promise<void> => {
  const authBase = import.meta.env.DEV ? '/auth-api' : (getEnv('VITE_AUTH_API_URL') || '/auth-api');
  const res = await fetch(`${authBase}/google/drive/file/${fileId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = await res.json();
  if (!res.ok || !json.status) {
    throw new Error(`Drive Delete fehlgeschlagen: ${json.message || res.status}`);
  }
};

export default {
  uploadImage,
  uploadToDrive,
  deleteDocument,
  deleteDriveFile,
};
